import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, ChevronUp, ChevronDown, ScrollText, MessageCircleQuestion, FlaskConical, Loader2, Pin, Send } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { Toggle, PageHead } from "@/components/kit";
import { DmChannelLimits } from "@/components/DmChannelLimits";

const field = "w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm";

function Counter({ n, max }) {
  return <span className={`text-[11px] ${n > max ? "text-red-400" : "text-zinc-600"}`}>{n}/{max}</span>;
}

function FaqRow({ f, i, last, limits, onChange, onMove, onDelete }) {
  return (
    <div data-testid={`faq-row-${i}`} className="bg-black/30 border border-zinc-800 rounded-lg p-3 flex gap-3">
      <div className="flex flex-col gap-1 pt-1">
        <button data-testid={`faq-up-${i}`} disabled={i === 0} onClick={() => onMove(i, -1)} className="text-zinc-500 hover:text-white disabled:opacity-20"><ChevronUp className="w-4 h-4" /></button>
        <button data-testid={`faq-down-${i}`} disabled={last} onClick={() => onMove(i, 1)} className="text-zinc-500 hover:text-white disabled:opacity-20"><ChevronDown className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          <input data-testid={`faq-q-${i}`} value={f.q} maxLength={limits.q} onChange={(e) => onChange(i, { ...f, q: e.target.value })} placeholder="Question — e.g. How do I get the Member role?" className={field} />
          <Counter n={f.q.length} max={limits.q} />
        </div>
        <div>
          <textarea data-testid={`faq-a-${i}`} rows={2} value={f.a} maxLength={limits.a} onChange={(e) => onChange(i, { ...f, a: e.target.value })} placeholder="Answer — e.g. React with ✅ in #verify." className={`${field} resize-y`} />
          <div className="text-right"><Counter n={f.a.length} max={limits.a} /></div>
        </div>
      </div>
      <button data-testid={`faq-delete-${i}`} onClick={() => onDelete(i)} className="text-zinc-500 hover:text-red-400 self-start pt-1"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

function TestBox({ guildId, draft }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (q.trim().length < 5) { toast.error("Write a question of at least 5 characters"); return; }
    setBusy(true); setRes(null);
    try { setRes((await api.post(`/guilds/${guildId}/ai-faq/test`, { question: q, draft })).data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="faq-test">
      <h3 className="font-head font-semibold mb-1 flex items-center gap-2"><FlaskConical className="w-4 h-4 text-volt" /> Test a question</h3>
      <p className="text-xs text-zinc-500 mb-3">Uses your current edits (even unsaved). Test answers aren't logged.</p>
      <div className="flex gap-2">
        <input data-testid="faq-test-input" value={q} maxLength={1000} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="What are the rules for self-promo?" className={field} />
        <button data-testid="faq-test-btn" onClick={run} disabled={busy} className="bg-zinc-800 hover:bg-zinc-700 rounded-md px-4 text-sm flex items-center gap-2 shrink-0">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ask"}</button>
      </div>
      {res && (
        <div data-testid="faq-test-result" className="mt-4 force-dark bg-[#2b2d31] rounded-md p-4 border-l-4 border-volt">
          {res.status === "ok" ? (
            <>
              <div className="text-xs text-zinc-300 font-semibold mb-2">✨ Crank.live AI Support{res.from_faq ? " · Server FAQ" : ""}</div>
              <p className="text-sm text-zinc-200 whitespace-pre-wrap">{res.answer}</p>
              <div className="text-[11px] text-zinc-500 mt-3 flex items-center gap-1">{res.from_faq && <><Pin className="w-3 h-3" /> Based on this server's FAQ ·</>} {res.confident ? "Confident answer" : "Not fully sure — would suggest a ticket"}</div>
            </>
          ) : <p className="text-sm text-red-400">{res.status === "not_configured" ? "No AI key is configured on the backend." : "The AI couldn't answer right now — try again shortly."}</p>}
        </div>
      )}
    </div>
  );
}

export default function AiHelp() {
  const { guildId } = useGuild();
  const [d, setD] = useState(null);
  const save = useUnsaved(d, setD, () => saveRaw());
  useEffect(() => { api.get(`/guilds/${guildId}/ai-faq`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [guildId]);
  if (!d) return <p className="text-zinc-500">Loading…</p>;
  const L = d.limits;
  const setFaqs = (faqs) => setD({ ...d, faqs });
  const move = (i, dir) => { const a = [...d.faqs]; [a[i], a[i + dir]] = [a[i + dir], a[i]]; setFaqs(a); };
  const draft = { enabled: d.enabled, dm_trigger: d.dm_trigger !== false, dm_trigger_word: (d.dm_trigger_word || "bb").trim(), dm_mention: !!d.dm_mention, dm_channel_mode: d.dm_channel_mode || "all", dm_channels: d.dm_channels || [], rules: d.rules, faqs: d.faqs.filter((f) => f.q.trim() && f.a.trim()) };
  const word = (d.dm_trigger_word ?? "bb") || "bb";
  const wordOk = /^[A-Za-z0-9!?.\-_$>]{1,20}$/.test(d.dm_trigger_word ?? "bb");
  const saveRaw = async () => {
    if (!wordOk) { toast.error("Fix the DM Assistant trigger word first"); return; }
    const incomplete = d.faqs.filter((f) => !!f.q.trim() !== !!f.a.trim()).length;
    if (incomplete) { toast.error(`${incomplete} FAQ entr${incomplete > 1 ? "ies are" : "y is"} missing a question or answer`); return; }
    try { setD((await api.put(`/guilds/${guildId}/ai-faq`, draft)).data); toast.success("AI Help FAQ saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="ai-help-page">
      <PageHead title="AI Help" desc="Teach /help AI Support about your server — FAQs and rules it uses when members ask questions here.">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm"><span className="text-zinc-400">Use server FAQ</span><Toggle testid="faq-enabled" checked={d.enabled} onChange={(v) => setD({ ...d, enabled: v })} /></div>
          <button data-testid="faq-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
        </div>
      </PageHead>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div data-testid="dm-assistant-card" className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 flex items-start gap-4">
            <Send className="w-5 h-5 text-volt shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-head font-semibold">DM Assistant <span className="text-xs font-mono text-zinc-500 font-normal">{word} &lt;message&gt;</span></h3>
              <p className="text-sm text-zinc-400 mt-1">Members type <span className="font-mono text-zinc-200">{word} how do I level up?</span> in any channel → the bot replies "Moving to DMs! 📩" and keeps chatting privately with memory (30 min idle, <span className="font-mono">reset</span> to end). Uses this server's FAQ and their own balance, level and tickets.</p>
              <div className={`mt-4 flex flex-wrap items-end gap-x-6 gap-y-3 ${d.dm_trigger !== false ? "" : "opacity-50 pointer-events-none"}`}>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-zinc-500">Trigger word</span>
                  <input data-testid="dm-trigger-word" value={d.dm_trigger_word ?? "bb"} maxLength={20}
                    onChange={(e) => setD({ ...d, dm_trigger_word: e.target.value.replace(/\s/g, "") })}
                    className={`mt-1 block w-40 bg-black border rounded-md px-3 py-1.5 text-sm font-mono focus:outline-none focus:border-volt ${wordOk ? "border-zinc-800" : "border-red-500"}`} />
                </label>
                <div className="flex items-center gap-2 text-sm pb-1.5"><Toggle testid="dm-mention" checked={!!d.dm_mention} onChange={(v) => setD({ ...d, dm_mention: v })} /><span className="text-zinc-400">Also trigger on @mention</span></div>
              </div>
              {d.dm_trigger !== false && <DmChannelLimits guildId={guildId} d={d} setD={setD} />}
              {!wordOk && <p data-testid="dm-trigger-word-error" className="text-xs text-red-400 mt-1.5">1–20 characters: letters, numbers and ! ? . - _ $ &gt; (no spaces or /)</p>}
            </div>
            <Toggle testid="dm-trigger-enabled" checked={d.dm_trigger !== false} onChange={(v) => setD({ ...d, dm_trigger: v })} />
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-semibold flex items-center gap-2"><MessageCircleQuestion className="w-4 h-4 text-volt" /> FAQ <span className="text-xs text-zinc-500 font-normal">{d.faqs.length}/{L.faqs}</span></h3>
              <button data-testid="faq-add" disabled={d.faqs.length >= L.faqs} onClick={() => setFaqs([...d.faqs, { id: "", q: "", a: "" }])} className="bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 rounded-md px-3 py-1.5 text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> Add entry</button>
            </div>
            {d.faqs.length === 0 && <p className="text-sm text-zinc-500" data-testid="faq-empty">No entries yet. Add questions members often ask — how to verify, event times, where to post clips…</p>}
            <div className="space-y-3">
              {d.faqs.map((f, i) => (
                <FaqRow key={f.id || `new-${i}`} f={f} i={i} last={i === d.faqs.length - 1} limits={L} onMove={move}
                  onChange={(idx, v) => setFaqs(d.faqs.map((x, j) => (j === idx ? v : x)))} onDelete={(idx) => setFaqs(d.faqs.filter((_, j) => j !== idx))} />
              ))}
            </div>
          </div>
          <TestBox guildId={guildId} draft={draft} />
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 h-fit space-y-3">
          <h3 className="font-head font-semibold flex items-center gap-2"><ScrollText className="w-4 h-4 text-volt" /> Server rules</h3>
          <textarea data-testid="faq-rules" rows={12} value={d.rules} maxLength={L.rules} onChange={(e) => setD({ ...d, rules: e.target.value })} placeholder={"1. Be respectful.\n2. No self-promo outside #promo.\n3. English only in #general."} className={`${field} resize-y`} />
          <div className="text-right"><Counter n={d.rules.length} max={L.rules} /></div>
          <p className="text-xs text-zinc-500">Only members of this server see answers based on this. It can't change how the AI behaves — just what it knows.</p>
          {d.updated_at && <p className="text-[11px] text-zinc-600" data-testid="faq-updated">Last saved {new Date(d.updated_at).toLocaleString()}{d.updated_by ? ` by ${d.updated_by}` : ""}</p>}
        </div>
      </div>
    </div>
  );
}
